"use client";

import {
	Button,
	type DefaultLinkProps,
	Heading,
	InputText,
	Link,
	Tag,
	Text
} from "@pamperedchef/design-system";
import type { FormEvent, MouseEvent } from "react";

import { RichText } from "@/composites/foundation";
import { CheckList } from "@/composites/landing/CheckList";
import { ResponsiveImage } from "@/composites/landing/ResponsiveImage";
import { SectionHeading } from "@/composites/landing/SectionHeading";
import type { Cta, CmsImage } from "@/composites/landing/types";
import * as styles from "./ClaimOfferForm.module.scss";

export interface ClaimFormFieldValues {
	firstName: string;
	lastName: string;
}

export type ClaimFormFieldErrors = Partial<
	Record<keyof ClaimFormFieldValues, string>
>;

export interface ClaimOfferFormProps {
	sectionHeading?: string;
	badge?: string;
	heading: string;
	/** HTML. A Terms & Conditions link opens the modal. */
	copy?: string;
	desktopImage?: CmsImage;
	mobileImage?: CmsImage;
	email?: string;
	emailLabel?: string;
	emailHelperText?: string;
	values: ClaimFormFieldValues;
	errors: ClaimFormFieldErrors;
	ctaLabel: string;
	/** HTML lead-in, e.g. "By signing up, you agree to our". */
	consentCopy?: string;
	/** Adds a Terms link to the consent line that opens the modal. */
	termsLinkLabel?: string;
	/** Adds an "and Privacy Policy" link to the consent line. */
	privacyPolicyLink?: Cta;
	benefitsHeading?: string;
	benefits?: string[];
	isSubmitting?: boolean;
	submitError?: string;
	onFieldChange(field: keyof ClaimFormFieldValues, value: string): void;
	onSubmit(): void;
	onTermsRequest(): void;
}

export const TERMS_LINK_HREF = "#terms";
const TERMS_LINK_TEXT = /terms\s*(&|and)\s*conditions/i;

/**
 * Sign-up card beside its photo. The offer email comes from the gift link and
 * is read-only; validation and submission live in the caller.
 */
export function ClaimOfferForm({
	sectionHeading,
	badge,
	heading,
	copy,
	desktopImage,
	mobileImage,
	email,
	emailLabel = "Offer email",
	emailHelperText,
	values,
	errors,
	ctaLabel,
	consentCopy,
	termsLinkLabel,
	privacyPolicyLink,
	benefitsHeading,
	benefits,
	isSubmitting = false,
	submitError,
	onFieldChange,
	onSubmit,
	onTermsRequest
}: ClaimOfferFormProps) {
	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		onSubmit();
	};

	// Authors link the words "Terms & Conditions" in the rich text; whatever
	// href they used, that click opens the modal instead of leaving the page.
	const handleCopyClick = (event: MouseEvent<HTMLElement>): void => {
		const anchor = (event.target as HTMLElement).closest("a");
		if (!anchor) return;
		if (
			anchor.getAttribute("href") === TERMS_LINK_HREF ||
			TERMS_LINK_TEXT.test(anchor.textContent ?? "")
		) {
			event.preventDefault();
			onTermsRequest();
		}
	};

	const hasConsent = Boolean(
		consentCopy || termsLinkLabel || privacyPolicyLink
	);
	const hasImage = Boolean(desktopImage || mobileImage);

	return (
		<section
			className={styles["claim-offer"]}
			data-component="ClaimOfferForm"
		>
			<SectionHeading headline={sectionHeading} />

			<div className={styles["claim-offer__layout"]}>
				{hasImage ? (
					<div className={styles["claim-offer__media"]}>
						<ResponsiveImage
							desktop={desktopImage}
							mobile={mobileImage}
						/>
					</div>
				) : null}

				<div className={styles["claim-offer__card"]}>
					{badge ? (
						<Tag
							kind="subtle"
							color="lime-green"
							size="md"
							label={badge}
							className={styles["claim-offer__eyebrow"]}
						/>
					) : null}

					<Heading
						tag="h3"
						className={styles["claim-offer__heading"]}
					>
						{heading}
					</Heading>

					{copy ? (
						<RichText
							content={copy}
							className={styles["claim-offer__copy"]}
							onClick={handleCopyClick}
						/>
					) : null}

					<form
						className={styles["claim-offer__form"]}
						onSubmit={handleSubmit}
						noValidate
						aria-busy={isSubmitting}
					>
						<div className={styles["claim-offer__row"]}>
							<InputText
								label="First Name"
								name="firstName"
								isRequired
								autoComplete="given-name"
								value={values.firstName}
								onChange={(value) =>
									onFieldChange("firstName", value)
								}
								isInvalid={Boolean(errors.firstName)}
								errorMessage={errors.firstName}
							/>
							<InputText
								label="Last Name"
								name="lastName"
								isRequired
								autoComplete="family-name"
								value={values.lastName}
								onChange={(value) =>
									onFieldChange("lastName", value)
								}
								isInvalid={Boolean(errors.lastName)}
								errorMessage={errors.lastName}
							/>
						</div>

						{email ? (
							<div
								className={styles["claim-offer__email"]}
								data-testid="offer-email"
							>
								<Text
									tag="p"
									styledAs="smb"
									className={
										styles["claim-offer__email-label"]
									}
								>
									{emailLabel}
								</Text>
								<Text
									tag="p"
									styledAs="mdr"
									className={
										styles["claim-offer__email-value"]
									}
								>
									{email}
								</Text>
								{emailHelperText ? (
									<Text
										tag="p"
										styledAs="xsr"
										className={
											styles["claim-offer__email-helper"]
										}
									>
										{emailHelperText}
									</Text>
								) : null}
							</div>
						) : null}

						{submitError ? (
							<Text
								tag="p"
								styledAs="smr"
								role="alert"
								className={styles["claim-offer__error"]}
							>
								{submitError}
							</Text>
						) : null}

						<Button
							type="submit"
							kind="primary"
							size="lg"
							isDisabled={isSubmitting}
							className={styles["claim-offer__cta"]}
						>
							{ctaLabel}
						</Button>

						{/* Not a <p>: the CMS lead-in is one, and the parser would close it. */}
						{hasConsent ? (
							<div
								className={styles["claim-offer__consent"]}
								data-testid="consent-line"
							>
								{consentCopy ? (
									<RichText
										content={consentCopy}
										tag="span"
									/>
								) : null}
								{termsLinkLabel ? (
									<>
										{" "}
										<Link<DefaultLinkProps>
											href={TERMS_LINK_HREF}
											size="sm"
											onClick={(event) => {
												event.preventDefault();
												onTermsRequest();
											}}
										>
											{termsLinkLabel}
										</Link>
									</>
								) : null}
								{privacyPolicyLink ? (
									<>
										{termsLinkLabel ? " and " : " "}
										<Link<DefaultLinkProps>
											href={privacyPolicyLink.href}
											size="sm"
										>
											{privacyPolicyLink.title}
										</Link>
									</>
								) : null}
								{termsLinkLabel || privacyPolicyLink
									? "."
									: null}
							</div>
						) : null}
					</form>

					{benefits?.length ? (
						<div className={styles["claim-offer__inbox"]}>
							<CheckList
								heading={benefitsHeading}
								items={benefits}
							/>
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
}
